// src/flows/FinishFlowAction.tsx
import React, { useMemo, useState } from 'react';
import { Button, Modal } from '../components/ui';
import { useSessionPersistence } from '../hooks/useSessionPersistence';
import type { FlowId } from '../utils/frameFlowMap';
import { FLOW_COMPLETION_CONFIG } from '../utils/flowCompletionConfig';

export const FinishFlowAction: React.FC<{
  flowId: FlowId;
  onFinished?: () => void;
  buttonText?: string;
  className?: string;
}> = ({ flowId, onFinished, buttonText = 'Continue to Dashboard', className }) => {
  const { session, requestCompleteTask, isTaskEmptyForCompletion } = useSessionPersistence();

  const cfg = useMemo(() => FLOW_COMPLETION_CONFIG[flowId], [flowId]);

  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  if (!cfg) {
    return (
      <Button disabled title="Completion rules not configured for this flow yet" className={className}>
        {buttonText}
      </Button>
    );
  }

  const isEmptyFlow = !cfg.hasMeaningfulData(session);

  const incompleteTaskIds = cfg.taskIdsToCheck.filter((id) => isTaskEmptyForCompletion(id));
  const incompleteLabels = incompleteTaskIds.map((id) => cfg.taskLabels?.[id] ?? id);

  const doFinish = async (args: { confirmEmpty: boolean; completionKind: 'with_data' | 'empty_flow' | 'empty_completion_task' }) => {
    const { confirmEmpty, completionKind } = args;

    console.log('[FinishFlowAction] doFinish called', { flowId, confirmEmpty, completionKind, completeTaskId: cfg.completeTaskId });

    setIsSubmitting(true);
    try {
      const completedWithData = cfg.hasMeaningfulData(session);
      console.log('[FinishFlowAction] hasMeaningfulData check:', { flowId, completedWithData });

      await requestCompleteTask(cfg.completeTaskId, {
        confirmEmpty,
        meta: {
          source: 'FinishFlowAction',
          flowId,
          completedAt: new Date().toISOString(),
          completionKind,
          completedWithData,
        },
      });

      console.log('[FinishFlowAction] requestCompleteTask completed successfully', { flowId, completeTaskId: cfg.completeTaskId });

      onFinished?.();
    } catch (error) {
      console.error('[FinishFlowAction] Error in doFinish:', error);
      throw error;
    } finally {
      setIsSubmitting(false);
      setOpen(false);
    }
  };

  const handleClick = () => {
    console.log('[FinishFlowAction] click', { flowId, isEmptyFlow, completeTaskId: cfg.completeTaskId });

    if (isEmptyFlow) {
      setOpen(true);
      return;
    }

    const completionTaskLooksEmpty = isTaskEmptyForCompletion(cfg.completeTaskId);

    void doFinish({
      confirmEmpty: completionTaskLooksEmpty,
      completionKind: completionTaskLooksEmpty ? 'empty_completion_task' : 'with_data',
    });
  };

  return (
    <>
      <Button
        onClick={handleClick}
        disabled={isSubmitting}
        className={[
          // Responsive footer NEXT button styling
          'flex-1 sm:flex-initial px-4 sm:px-6 py-3 rounded inline-flex justify-center items-center gap-2.5 transition-colors whitespace-nowrap min-w-0',
          isSubmitting ? 'bg-cyan-700 opacity-50 cursor-not-allowed' : 'bg-cyan-700 hover:bg-cyan-800 cursor-pointer opacity-100',
          // allow caller override
          className ?? '',
        ].join(' ')}
      >
        <span className="text-center text-stone-50 text-sm sm:text-base font-medium font-['Work_Sans'] leading-none truncate">
          {isSubmitting ? 'Finishing…' : buttonText}
        </span>
      </Button>
      <Modal open={open} onClose={() => setOpen(false)}>
        <div className="space-y-4">
          <div>
            <h3 className="text-lg font-semibold text-stone-900">{cfg.emptyModal.title}</h3>
            <p className="text-sm text-stone-600 mt-2">{cfg.emptyModal.body}</p>

            {incompleteLabels.length > 0 && (
              <ul className="mt-3 text-sm text-stone-700 list-disc pl-5 space-y-1">
                
                {incompleteTaskIds.map((id, idx) => {
                const label = incompleteLabels[idx];
                return <li key={id}>{label} not completed</li>;
                })}

              </ul>
            )}
          </div>

          <div className="flex gap-3 justify-end">
            <Button variant="outline" onClick={() => setOpen(false)} disabled={isSubmitting}>
              {cfg.emptyModal.cancelText}
            </Button>

            <Button onClick={() => void doFinish({ confirmEmpty: true, completionKind: 'empty_flow' })} disabled={isSubmitting}>
              {isSubmitting ? 'Finishing…' : cfg.emptyModal.confirmText}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};